import { useEffect, useState } from "react";
import {
  Activity,
  BarChart3,
  Bot,
  BriefcaseBusiness,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  X,
  KeyRound,
  LogOut,
  PackageOpen,
  RadioTower,
  Rocket,
  Settings,
  ServerCog,
  ShieldCheck,
  Users,
  UserCog,
  UsersRound,
} from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { BrandLogo } from "./BrandLogo";
import { hermesCodexConnectionRequired, type HermesReadinessItem } from "../lib/hermes-onboarding";

export const sidebarLinks = [
  { to: "/commercial-dashboard", label: "Dashboard comercial", icon: BarChart3, roles: ["ADMIN", "CLOSER_ADMIN"] },
  { to: "/closer", label: "Bandeja comercial", icon: BriefcaseBusiness, roles: ["ADMIN", "CLOSER_ADMIN", "CLOSER_OPERATOR"] },
  { to: "/campaigns", label: "Campañas", icon: Rocket },
  { to: "/prospects", label: "Prospectos", icon: Users },
  { to: "/offers", label: "Ofertas", icon: PackageOpen },
  { to: "/meeting-team", label: "Equipo de reuniones", icon: UsersRound },
  { to: "/operation", label: "Operación", icon: Activity },
  { to: "/approvals", label: "Aprobaciones", icon: CheckCheck },
  { to: "/integrations", label: "Configuración", icon: Settings },
  { to: "/agents", label: "Agentes", icon: Bot },
  { to: "/users", label: "Usuarios", icon: UserCog, roles: ["ADMIN"] },
  { to: "/system", label: "Sistema", icon: ServerCog, roles: ["ADMIN"] },
] as const;

export function linksForRole(role?: string) {
  if (role === "CLOSER_ADMIN" || role === "CLOSER_OPERATOR") return sidebarLinks.filter((link) => link.to === "/closer" || (role === "CLOSER_ADMIN" && link.to === "/commercial-dashboard"));
  return sidebarLinks.filter((link) => !("roles" in link) || (link.roles as readonly string[]).includes(role ?? ""));
}

export function SidebarNavigation({ collapsed, role, updateAvailable = false }: { collapsed: boolean; role?: string; updateAvailable?: boolean }) {
  return <nav aria-label="Secciones">
    {linksForRole(role).map(({ to, label, icon: Icon }) => (
      <NavLink key={to} to={to} title={collapsed ? label : undefined} aria-label={label}>
        <Icon size={19} aria-hidden="true" />
        <span>{label}</span>
        {to === "/system" && updateAvailable && <span className="system-update-dot" role="img" aria-label="Actualización disponible" />}
      </NavLink>
    ))}
  </nav>;
}

export function updateNoticeStorageKey(input: { available?: boolean; version?: string | null; instanceId?: string } | undefined, user: { id?: string; role?: string } | undefined) {
  if (!input?.available || !input.instanceId || !user?.id || user.role !== "ADMIN") return null;
  // Availability is useful even during the short interval in which the lease
  // has not exposed its version yet. "pending" is intentionally a different
  // key so the identified release is shown again as soon as it arrives.
  const release = input.version?.trim() || "pending";
  return `vicnet:update-notice:${input.instanceId}:${user.id}:${release}`;
}

export function UpdateAvailableNotice({ version, onDismiss }: { version?: string | null; onDismiss: () => void }) {
  return <div className="app-update-toast" role="status" aria-live="polite">
    <span className="app-update-toast-icon"><ServerCog size={19} /></span>
    <NavLink to="/system"><strong>{version ? `Versión ${version} disponible` : "Nueva versión disponible"}</strong><small>Ver cambios e instalar</small></NavLink>
    <button type="button" aria-label="Cerrar aviso de actualización" title="Cerrar aviso" onClick={onDismiss}><X size={17} /></button>
  </div>;
}

export function LogoutConfirmation({ pending, error, onCancel, onConfirm }: { pending: boolean; error: string; onCancel: () => void; onConfirm: () => void }) {
  return <div className="modal-backdrop logout-confirmation-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !pending) onCancel(); }}>
    <section className="modal logout-confirmation" role="dialog" aria-modal="true" aria-labelledby="logout-confirmation-title">
      <span className="logout-confirmation-icon"><LogOut size={22} /></span>
      <div><span className="eyebrow">SESIÓN</span><h2 id="logout-confirmation-title">¿Cerrar sesión?</h2><p>Vas a volver a la pantalla de acceso. El trabajo guardado no se modifica.</p></div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="modal-actions"><button type="button" className="secondary" disabled={pending} autoFocus onClick={onCancel}>Cancelar</button><button type="button" className="primary" disabled={pending} onClick={onConfirm}>{pending ? "Cerrando…" : "Cerrar sesión"}</button></div>
    </section>
  </div>;
}

export function Shell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("vicnet:sidebar-collapsed") === "true"; } catch { return false; }
  });
  const navigate = useNavigate();
  const client = useQueryClient();
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () =>
      api<{ user: { id: string; username: string; displayName: string; role: string } }>("/auth/me"),
    retry: false,
  });
  const { data: health } = useQuery({
    queryKey: ["health"],
    queryFn: () =>
      api<{
        status: string;
        providers: Record<string, string>;
        outreachMode: "DRY_RUN" | "TEST" | "PRODUCTION";
        appliance?: { enabled: boolean; instanceId: string; status: string; update: { available: boolean; version: string | null }; commercialOperationsAllowed: boolean };
      }>("/health"),
    refetchInterval: 15000,
  });
  const { data: readiness } = useQuery({
    queryKey: ["integrations-readiness"],
    queryFn: () => api<{ items: HermesReadinessItem[] }>("/integrations/readiness"),
    refetchInterval: 30000,
  });
  useEffect(() => {
    const stream = new EventSource("/api/events", { withCredentials: true });
    let refreshTimer: number | undefined;
    stream.onmessage = () => {
      if (refreshTimer !== undefined) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = undefined;
        void Promise.all([
          client.invalidateQueries({ queryKey: ["campaigns"] }),
          client.invalidateQueries({ queryKey: ["campaign"] }),
          client.invalidateQueries({ queryKey: ["run"] }),
          client.invalidateQueries({ queryKey: ["approvals"] }),
          client.invalidateQueries({ queryKey: ["prospects"] }),
          client.invalidateQueries({ queryKey: ["prospect-activity"] }),
          client.invalidateQueries({ queryKey: ["meeting-team"] }),
          client.invalidateQueries({ queryKey: ["closer-opportunities"] }),
        ]);
      }, 250);
    };
    return () => {
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      stream.close();
    };
  }, [client]);
  useEffect(() => {
    try { localStorage.setItem("vicnet:sidebar-collapsed", String(sidebarCollapsed)); } catch { /* storage may be disabled */ }
  }, [sidebarCollapsed]);
  useEffect(() => {
    if (!logoutConfirmationOpen) return;
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !logoutPending) {
        setLogoutConfirmationOpen(false);
        setLogoutError("");
      }
    };
    document.addEventListener("keydown", closeWithEscape);
    return () => document.removeEventListener("keydown", closeWithEscape);
  }, [logoutConfirmationOpen, logoutPending]);
  const degradedProviders = Object.values(health?.providers ?? {}).filter((status) => ["ERROR", "DEGRADED", "DISCONNECTED"].includes(status)).length;
  const productionOutreach = health?.outreachMode === "PRODUCTION";
  const deliveryControlLabel = productionOutreach ? "ENVÍO PRODUCTIVO" : "SAFE SEND";
  const deliveryControlDetail = productionOutreach ? "Destinatarios reales" : "Control técnico activo";
  const codexConnectionPending = Boolean(
    health?.appliance?.enabled &&
    readiness &&
    hermesCodexConnectionRequired(readiness.items),
  );
  const updateNoticeKey = updateNoticeStorageKey(health?.appliance ? {
    available: health.appliance.update.available,
    version: health.appliance.update.version,
    instanceId: health.appliance.instanceId,
  } : undefined, me?.user);
  const [dismissedUpdateKey, setDismissedUpdateKey] = useState<string | null>(() => {
    try { return localStorage.getItem("vicnet:dismissed-update-notice"); } catch { return null; }
  });
  useEffect(() => {
    try { setDismissedUpdateKey(localStorage.getItem("vicnet:dismissed-update-notice")); }
    catch { setDismissedUpdateKey(null); }
  }, [updateNoticeKey]);
  const showUpdateNotice = Boolean(updateNoticeKey && dismissedUpdateKey !== updateNoticeKey);
  function dismissUpdateNotice() {
    if (!updateNoticeKey) return;
    try { localStorage.setItem("vicnet:dismissed-update-notice", updateNoticeKey); } catch { /* storage may be disabled */ }
    setDismissedUpdateKey(updateNoticeKey);
  }
  async function confirmLogout() {
    if (logoutPending) return;
    setLogoutPending(true);
    setLogoutError("");
    try {
      await api("/auth/logout", { method: "POST" });
      client.clear();
      navigate("/login");
    } catch {
      setLogoutError("No se pudo cerrar la sesión. Volvé a intentarlo.");
      setLogoutPending(false);
    }
  }
  return (
    <div className={`app-shell${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      <aside aria-label="Navegación principal">
        <div className="sidebar-brand-row">
          <BrandLogo variant={sidebarCollapsed ? "login" : "horizontal"} />
          <button
            type="button"
            className="sidebar-toggle"
            aria-label={sidebarCollapsed ? "Expandir menú lateral" : "Contraer menú lateral"}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
            onClick={() => setSidebarCollapsed((value) => !value)}
          >
            {sidebarCollapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
          </button>
        </div>
        <SidebarNavigation collapsed={sidebarCollapsed} role={me?.user.role} updateAvailable={health?.appliance?.update.available} />
        <div className="aside-foot">
          <div className="safe-card" title={sidebarCollapsed ? `${deliveryControlLabel} · ${deliveryControlDetail}` : undefined} aria-label={`${deliveryControlLabel}, ${deliveryControlDetail}`}>
            <ShieldCheck size={18} />
            <div>
              <strong>{deliveryControlLabel}</strong>
              <small>{deliveryControlDetail}</small>
            </div>
          </div>
          <button
            className="profile"
            title={sidebarCollapsed ? `${me?.user.displayName ?? "Usuario"} · Cerrar sesión` : "Cerrar sesión"}
            aria-label={`${me?.user.displayName ?? "Usuario"}: cerrar sesión`}
            onClick={() => { setLogoutError(""); setLogoutConfirmationOpen(true); }}
          >
            <span>{me?.user.displayName?.slice(0, 1) ?? "V"}</span>
            <div>
              <strong>{me?.user.displayName ?? "Usuario"}</strong>
              <small>{me?.user.role ?? ""}</small>
            </div>
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main>
        <header>
          <div className="shell-status-group"><div className={`outreach-mode mode-${(health?.outreachMode ?? "DRY_RUN").toLowerCase()}`}>OUTREACH {health?.outreachMode ?? "DRY_RUN"}</div>{health?.appliance && !health.appliance.commercialOperationsAllowed && <NavLink className="instance-suspended" to="/system">Instancia en solo lectura</NavLink>}</div>
          <div className="health">
            <RadioTower size={16} />
            <span className={health?.status === "ok" ? "dot ok" : "dot"} />
            {health?.status === "ok"
              ? "Plataforma operativa"
              : degradedProviders
                ? `${degradedProviders} servicio${degradedProviders === 1 ? "" : "s"} requiere${degradedProviders === 1 ? "" : "n"} atención`
                : "Verificando servicios"}
          </div>
        </header>
        {showUpdateNotice && <UpdateAvailableNotice version={health!.appliance!.update.version} onDismiss={dismissUpdateNotice} />}
        {codexConnectionPending && <NavLink className="global-onboarding" to="/integrations"><KeyRound size={17} /><span><strong>Falta conectar OpenAI/Codex</strong><small>Autorizá la cuenta que usarán los agentes.</small></span><ChevronRight size={17} /></NavLink>}
        <div className="page">
          <Outlet />
        </div>
      </main>
      {logoutConfirmationOpen && <LogoutConfirmation pending={logoutPending} error={logoutError} onCancel={() => { setLogoutConfirmationOpen(false); setLogoutError(""); }} onConfirm={() => void confirmLogout()} />}
    </div>
  );
}
